import { useSearchParams } from 'next/navigation';
import { ReactElement, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { animateScroll as scroll } from 'react-scroll';

import { CalmTheme, FocusRingVariants } from '@calm-web/design-system';

import { useStripeElements } from '@/components/purchase/StripeElementsProvider';
import ValuePropsList from '@/components/valuePropList';
import { useAnalytics } from '@/hooks/analytics';
import { useDeviceState, usePurchaseParamsState } from '@/hooks/store';
import genericMessages from '@/messages/messages';

import AnimatedScreenWrapper from '../../Components/AnimatedScreenWrapper';
import LimitedTimeCallout from '../../Components/LimitedTimeCallout';
import PaymentFormModule from '../../Components/PaymentFormModule';
import {
	basicLineItems,
	upsellCopy as controlMessages,
	extendedTrialPromotions,
	premiumLineItems,
} from '../../ConfigData/MessageSets';
import messages from './messages';
import { Button, Heading, PaymentFormWrapper, ScrollingContainer, Wrapper } from './styles';

interface Props {
	onContinueOverride?: () => void;
	showOnlyBenefits?: boolean;
	continueText?: string;
}

function PremiumUpsell({ onContinueOverride, continueText }: Props): ReactElement {
	const { formatMessage } = useIntl();
	const { logEvent } = useAnalytics();
	const formRef = useRef<HTMLDivElement>(null);
	const [activeScreen, setActiveScreen] = useState('upsell');

	const { isMobile } = useDeviceState();
	const { promotion, purchaseType } = usePurchaseParamsState();
	const searchParams = useSearchParams();

	const { isStripeNextEnabled } = useStripeElements();
	const isSecondFreeTrial = searchParams?.get('source') === 'second_free_trial_offer';

	const isExtendedTrialPromotion = extendedTrialPromotions.includes(promotion || '');
	const showCallout: boolean = purchaseType?.type !== 'monthly';

	function onShowPaymentForm(): void {
		logEvent({
			eventName: 'Upsell : Proceeded',
			eventProps: {
				screen: 'Upsell',
			},
		});

		logEvent({
			eventName: 'Screen : Viewed',
			eventProps: {
				screen: 'Free Trial',
				active_flow: 'meditate',
			},
		});

		logEvent({
			eventName: 'Subscribe : Purchase : Form : Landed',
			eventProps: {
				...(promotion && { promotion }),
				is_stripe_next: isStripeNextEnabled,
			},
		});
		if (onContinueOverride) {
			onContinueOverride();
		} else {
			setActiveScreen('payment');
		}
	}

	const isFormVisible = activeScreen === 'payment';

	useEffect(() => {
		if (isFormVisible && formRef?.current) {
			scroll.scrollTo(
				(formRef?.current?.offsetTop ?? 0) +
					(isMobile ? CalmTheme.mobileNavigation.height : CalmTheme.navigation.height),
				{
					smooth: 'easeInOutCubic',
					delay: 50,
					duration: 750,
				},
			);
		}
	}, [isFormVisible, isMobile]);

	const buildHeading = () => {
		if (purchaseType?.type === 'monthly') {
			return <Heading>{formatMessage(messages.directSubHeading)}</Heading>;
		}
		let amount = purchaseType?.duration ?? '14';
		if (isExtendedTrialPromotion && promotion) {
			amount = promotion.slice(0, 2);
		}
		return (
			<Heading>
				{formatMessage(messages.extendedTrialHeading, {
					amount,
					br: isMobile ? <br /> : '',
				})}
			</Heading>
		);
	};

	return (
		<>
			<AnimatedScreenWrapper isExiting={false} announce={false}>
				<Wrapper role={isFormVisible ? 'region' : 'alert'} aria-live={isFormVisible ? 'off' : 'polite'}>
					{showCallout && <LimitedTimeCallout>{formatMessage(messages.callout)}</LimitedTimeCallout>}
					{buildHeading()}
					<ValuePropsList
						title={formatMessage(controlMessages.includedBasic, {
							br: <br></br>,
						})}
						copy={basicLineItems}
						buttonColor="gray4"
					/>
					<ValuePropsList
						title={formatMessage(controlMessages.includedPremium, {
							br: <br></br>,
						})}
						copy={premiumLineItems}
						buttonColor="blue3"
					/>
					{!isFormVisible && (
						<Button
							data-testid="upsell-continue-button"
							backgroundColor={'gradientTabBlue'}
							textColor="white"
							onPress={onShowPaymentForm}
							focusRingVariant={FocusRingVariants.Dark}
						>
							{continueText || formatMessage(genericMessages.continue)}
						</Button>
					)}
				</Wrapper>
			</AnimatedScreenWrapper>
			<ScrollingContainer ref={formRef} isVisible={isFormVisible}>
				<PaymentFormWrapper isVisible={isFormVisible}>
					<PaymentFormModule isVisible={isFormVisible} isSecondFreeTrial={isSecondFreeTrial} />
				</PaymentFormWrapper>
			</ScrollingContainer>
		</>
	);
}

export default PremiumUpsell;
